import { toast } from "react-toastify";

/**
 * Gère les erreurs de l'API de manière uniforme
 * @param {Error} error L'erreur interceptée
 * @param {string} context Le contexte de l'erreur
 * @param {Function} fallbackFn Fonction de repli pour générer des données par défaut
 * @param {boolean} showToast Afficher ou non un toast d'erreur
 * @returns {Object} Données de repli ou rethrow l'erreur
 */
export const handleApiError = (
  error,
  context,
  fallbackFn,
  showToast = false,
) => {
  let errorMessage = "Une erreur est survenue";
  let errorCode = "ERROR";
  let shouldRethrow = false;

  // Déterminer le type d'erreur
  if (!error.response) {
    // Erreur réseau
    errorMessage = "Problème de connexion au serveur";
    errorCode = "NETWORK_ERROR";
  } else {
    // Erreur avec réponse du serveur
    const status = error.response.status;
    const data = error.response.data || {};

    errorMessage = data.message || error.message || "Erreur serveur";
    errorCode = data.code || `HTTP_${status}`;

    // Gérer les codes d'erreur spécifiques
    switch (status) {
      case 401:
        errorMessage = "Authentification requise";
        errorCode = "UNAUTHORIZED";
        break;
      case 403:
        errorMessage = "Accès refusé";
        errorCode = "FORBIDDEN";
        break;
      case 404:
        errorMessage = "Ressource introuvable";
        errorCode = "NOT_FOUND";
        break;
      case 429:
        errorMessage = "Trop de requêtes, veuillez réessayer plus tard";
        errorCode = "RATE_LIMITED";
        shouldRethrow = true; // Ces erreurs doivent être gérées par la logique de retry
        break;
      case 500:
        errorMessage = "Erreur serveur interne";
        errorCode = "SERVER_ERROR";
        break;
    }
  }

  // Journaliser l'erreur
  console.error(`[${context}] ${errorCode}: ${errorMessage}`, error);

  // Afficher un toast si nécessaire
  if (showToast) {
    toast.error(errorMessage);
  }

  // Si c'est une erreur 429, la propager pour permettre la logique de retry
  if (shouldRethrow) {
    throw error;
  }

  // Retourner les données de secours
  return fallbackFn
    ? fallbackFn()
    : { error: errorCode, message: errorMessage };
};

/**
 * Génère des données de repli pour différents types de ressources
 * @param {string} resourceType Type de ressource
 * @returns {Object} Données de repli
 */
export const generateFallbackData = (resourceType) => {
  switch (resourceType) {
    case "wallet.balance":
      return {
        balance: { RUB: 0, XOF: 0, BBT: 0, USDT: 0 },
        message: "Solde temporairement indisponible",
      };
    case "user.profile":
      return {
        user: {
          name: "Utilisateur",
          email: "",
          role: "user",
          isActive: true,
        },
        message: "Profil temporairement indisponible",
      };
    default:
      return {
        data: null,
        message: "Données temporairement indisponibles",
      };
  }
};
